import React, { useState, useEffect, useMemo, memo } from 'react'
import TableOperationsUI from '../../../core-system/Table/TableOperationsUI'
import {
  filterRows,
  searchRows,
  sortRows,
} from '../../../core-system/Table/helperFunctions'
import { FilterCheckbox } from '../../../core-system/Table/FilterModel'
import { uniq, find } from 'lodash-es'
import { getProgramNamesFromType, getCommuterTagFilterOptions, COMMUTER_SEARCH_KEYS } from 'features/Segments/helperFunctions'
import { RosterFilterSelection } from 'redux/employee/rosterQuery'

export interface RosterFilterOptions {
  tags: string[]
  groups: string[]
  programs: string[]
  competitions: string[]
}

interface CommuterTableOperationsProps {
  commuters: any[]
  setFilteredCommuters?: (rows: any[]) => void
  serverFilters?: RosterFilterOptions
  onServerQueryChange?: (query: RosterFilterSelection) => void
  hideFilters?: boolean
  initialQuery?: RosterFilterSelection
}

const appliedFiltersFromQuery = (
  query?: RosterFilterSelection
): FilterCheckbox[] => {
  if (!query) {
    return []
  }
  return [
    ...query.hasRegistered.map((value) => ({
      label: '',
      checked: true,
      key: 'hasRegistered',
      value,
    })),
    ...query.hasInvite.map((value) => ({
      label: '',
      checked: true,
      key: 'hasInvite',
      value,
    })),
    ...query.tags.map((tag) => ({
      label: tag,
      checked: true,
      key: 'tagNames',
      value: tag,
    })),
    ...query.groups.map((group) => ({
      label: group,
      checked: true,
      key: 'segmentNames',
      value: group,
    })),
    ...query.programs.map((program) => ({
      label: program,
      checked: true,
      key: 'programsEnrolled',
      value: program,
    })),
    ...query.competitions.map((competition) => ({
      label: competition,
      checked: true,
      key: 'competitionsEnrolled',
      value: competition,
    })),
  ]
}

const statusFilters = [
  {
    label: 'Registered',
    checked: false,
    key: 'hasRegistered',
    value: true,
  },
  { label: 'Invited', checked: false, key: 'hasInvite', value: true },
  {
    label: 'Needs Invite',
    checked: false,
    key: 'hasInvite',
    value: false,
  },
]

const checkbox = (label: string, key: string, value: string) => ({
  label,
  checked: false,
  key,
  value,
})

const serverQueryFromFilters = (
  search: string,
  ordering: RosterFilterSelection['ordering'],
  selected: FilterCheckbox[]
): RosterFilterSelection => ({
  search,
  ordering,
  hasRegistered: selected
    .filter((item) => item.key === 'hasRegistered')
    .map((item) => Boolean(item.value)),
  hasInvite: selected
    .filter((item) => item.key === 'hasInvite')
    .map((item) => Boolean(item.value)),
  tags: selected
    .filter((item) => item.key === 'tagNames')
    .map((item) => String(item.value)),
  groups: selected
    .filter((item) => item.key === 'segmentNames')
    .map((item) => String(item.value)),
  programs: selected
    .filter((item) => item.key === 'programsEnrolled')
    .map((item) => String(item.value)),
  competitions: selected
    .filter((item) => item.key === 'competitionsEnrolled')
    .map((item) => String(item.value)),
})

const CommuterTableOperations = memo(
  ({
    commuters,
    setFilteredCommuters,
    serverFilters,
    onServerQueryChange,
    hideFilters = false,
    initialQuery,
  }: CommuterTableOperationsProps) => {
    const isServer = Boolean(onServerQueryChange)
    const restoredFilters = appliedFiltersFromQuery(initialQuery)
    // Local state for displayed rows and filters/sort
    const [filterSet, setFilterSet] = useState(restoredFilters.length > 0)
    const [searchSet, setSearchSet] = useState(false)
    const [searchManipulatedRows, setSearchManipulatedRows] = useState<any[]>(
      []
    )
    const [filterManipulatedRows, setFilterManipulatedRows] = useState<any[]>(
      []
    )
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
      initialQuery?.ordering === 'name'
        ? 'asc'
        : initialQuery?.ordering === '-name'
          ? 'desc'
          : null
    )
    const [searchValue, setSearchValue] = useState(initialQuery?.search || '')
    const [isFilterButtonActive, setIsFilterButtonActive] = useState(
      restoredFilters.length > 0
    )
    const [isSearchActive, setIsSearchActive] = useState(
      Boolean(initialQuery?.search)
    )
    const [appliedFilters, setAppliedFilters] =
      useState<FilterCheckbox[]>(restoredFilters)

    const currentOrdering = (): RosterFilterSelection['ordering'] =>
      sortDirection === 'asc' ? 'name' : sortDirection === 'desc' ? '-name' : ''

    // Resetting everything if original commuters change
    // This is done because we have quick filters on the commuters table
    // and we want to reset the filters when the commuters change
    useEffect(() => {
      if (isServer) {
        return
      }
      setSearchManipulatedRows([])
      setFilterManipulatedRows([])
      setSortDirection(null)
      setSearchSet(false)
      setFilterSet(false)
      setSearchValue('')
      setIsFilterButtonActive(false)
      setIsSearchActive(false)
    }, [commuters, isServer])

    // Get the final rows to display
    // getting the common rows between search, filter, and sort
    const finalRows = () => {
      // If nothing is set, return all rows
      if (!searchSet && !filterSet && !sortDirection) {
        return commuters
      }
      // If only sort is set, return sorted rows
      if (!searchSet && !filterSet && sortDirection) {
        return sortRows(commuters, 'name', sortDirection)
      }
      const commonRows = []
      // If any row is empty the result will be empty
      if (searchSet && searchManipulatedRows.length === 0) {
        return []
      }
      if (filterSet && filterManipulatedRows.length === 0) {
        return []
      }
      if (searchManipulatedRows.length)
        searchManipulatedRows.forEach((row) => {
          if (filterManipulatedRows.length) {
            if (
              find(filterManipulatedRows, { id: row.id }) &&
              !find(commonRows, { id: row.id })
            )
              commonRows.push(row)
          } else {
            commonRows.push(row)
          }
        })
      if (filterManipulatedRows.length)
        filterManipulatedRows.forEach((row) => {
          if (searchManipulatedRows.length) {
            if (
              find(searchManipulatedRows, { id: row.id }) &&
              !find(commonRows, { id: row.id })
            )
              commonRows.push(row)
          } else {
            commonRows.push(row)
          }
        })
      return sortRows(commonRows, 'name', sortDirection)
    }

    // Push up the current rows whenever they change
    useEffect(() => {
      if (isServer || !setFilteredCommuters) {
        return
      }
      setFilteredCommuters(finalRows())
    }, [searchManipulatedRows, filterManipulatedRows, sortDirection, isServer])

    // Build filter options
    const emitServerQuery = (
      search: string,
      ordering: RosterFilterSelection['ordering'],
      selected: FilterCheckbox[] = []
    ) => {
      onServerQueryChange?.(serverQueryFromFilters(search, ordering, selected))
    }

    const filters = useMemo(() => {
      if (serverFilters) {
        return [
          { header: 'Status', checkboxes: statusFilters },
          ...(serverFilters.tags.length
            ? [
                {
                  header: 'Tags',
                  checkboxes: serverFilters.tags.map((tag) =>
                    checkbox(tag, 'tagNames', tag)
                  ),
                },
              ]
            : []),
          ...(serverFilters.groups.length
            ? [
                {
                  header: 'Groups',
                  checkboxes: serverFilters.groups.map((group) =>
                    checkbox(group, 'segmentNames', group)
                  ),
                },
              ]
            : []),
          ...(serverFilters.programs.length
            ? [
                {
                  header: 'Programs',
                  checkboxes: serverFilters.programs.map((program) =>
                    checkbox(
                      getProgramNamesFromType(program.toLowerCase()),
                      'programsEnrolled',
                      program
                    )
                  ),
                },
              ]
            : []),
          ...(serverFilters.competitions.length
            ? [
                {
                  header: 'Competitions',
                  checkboxes: serverFilters.competitions.map((competition) =>
                    checkbox(competition, 'competitionsEnrolled', competition)
                  ),
                },
              ]
            : []),
        ]
      }
      const groups: string[] = []
      const programs: string[] = []
      const competitions: string[] = []

      commuters.forEach((c) => {
        if (c.segmentNames) groups.push(...c.segmentNames)
        if (c.programsEnrolled) programs.push(...c.programsEnrolled)
        if (c.competitionsEnrolled) competitions.push(...c.competitionsEnrolled)
      })
      const tagFilterSection = getCommuterTagFilterOptions(commuters)

      return [
        {
          header: 'Status',
          checkboxes: [
            {
              label: 'Registered',
              checked: false,
              key: 'hasRegistered',
              value: true,
            },
            { label: 'Invited', checked: false, key: 'hasInvite', value: true },
            {
              label: 'Needs Invite',
              checked: false,
              key: 'hasInvite',
              value: false,
            },
          ],
        },
        ...(tagFilterSection ? [tagFilterSection] : []),
        ...(groups.length
          ? [
              {
                header: 'Groups',
                checkboxes: uniq(groups).map((g) => ({
                  label: g,
                  checked: false,
                  key: 'segmentNames',
                  value: g,
                })),
              },
            ]
          : []),
        ...(programs.length
          ? [
              {
                header: 'Programs',
                checkboxes: uniq(programs).map((p) => ({
                  label: getProgramNamesFromType(p.toLowerCase()),
                  checked: false,
                  key: 'programsEnrolled',
                  value: p,
                })),
              },
            ]
          : []),
        ...(competitions.length
          ? [
              {
                header: 'Competitions',
                checkboxes: uniq(competitions).map((c) => ({
                  label: c,
                  checked: false,
                  key: 'competitionsEnrolled',
                  value: c,
                })),
              },
            ]
          : []),
      ]
    }, [commuters, serverFilters])

    // Handlers
    const handleApplyFilters = (filters: FilterCheckbox[]) => {
      if (isServer) {
        setAppliedFilters(filters)
        setFilterSet(true)
        setIsFilterButtonActive(true)
        emitServerQuery(searchValue, currentOrdering(), filters)
        return
      }
      const filtered = filterRows(commuters, filters)
      setFilterManipulatedRows(filtered)
      setFilterSet(true)
      setIsFilterButtonActive(true)
    }

    const handleFiltersReset = () => {
      if (isServer) {
        setAppliedFilters([])
        setFilterSet(false)
        setIsFilterButtonActive(false)
        emitServerQuery(searchValue, currentOrdering(), [])
        return
      }
      setFilterManipulatedRows([])
      setFilterSet(false)
      setIsFilterButtonActive(false)
    }

    const handleSearch = (value: string) => {
      if (isServer) {
        setSearchValue(value)
        setSearchSet(Boolean(value))
        setIsSearchActive(Boolean(value))
        emitServerQuery(value, currentOrdering(), appliedFilters)
        return
      }
      if (value) {
        const searched = searchRows(
          commuters,
          [...COMMUTER_SEARCH_KEYS],
          value
        )
        setSearchManipulatedRows(searched)
        setSearchSet(true)
        setSearchValue(value)
        setIsSearchActive(true)
      } else {
        setSearchManipulatedRows([])
        setSearchSet(false)
        setSearchValue('')
        setIsSearchActive(false)
      }
    }

    const handleSearchClear = () => {
      if (isServer) {
        setSearchValue('')
        setSearchSet(false)
        setIsSearchActive(false)
        emitServerQuery('', currentOrdering(), appliedFilters)
        return
      }
      setSearchManipulatedRows([])
      setSearchSet(false)
      setSearchValue('')
      setIsSearchActive(false)
    }

    const handleSortAsc = () => {
      setSortDirection('asc')
      if (isServer) {
        emitServerQuery(searchValue, 'name', appliedFilters)
      }
    }

    const handleSortDesc = () => {
      setSortDirection('desc')
      if (isServer) {
        emitServerQuery(searchValue, '-name', appliedFilters)
      }
    }

    return (
      <TableOperationsUI
        handleSearch={handleSearch}
        handleSearchClear={handleSearchClear}
        onAscendingClick={handleSortAsc}
        onDescendingClick={handleSortDesc}
        filters={filters}
        handleApplyFilters={handleApplyFilters}
        handleFiltersReset={handleFiltersReset}
        isFilterButtonActive={isFilterButtonActive}
        activeSortDirection={sortDirection}
        isSearchActive={isSearchActive}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        showFilter={!hideFilters}
      />
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  CommuterTableOperations.displayName = 'CommuterTableOperations'
}

export default CommuterTableOperations
